import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';

import type { ApiEnvelope } from '../http/api-envelope';
import { SKIP_AUTH } from './auth.context';
import type {
  ConfirmEmailRequest,
  ConfirmEmailResponse,
  CreateSessionRequest,
  CurrentUserResponse,
  RegisterRequest,
  RegisterResponse,
  ResendConfirmationRequest,
  ResendConfirmationResponse,
  SetLanguageRequest,
  SetWorkspaceRequest,
  TokenResponse,
} from './auth.contract';
import {
  toAuthToken,
  toAuthUser,
  toEmailConfirmation,
  toRegisterPayload,
  toRegistrationResult,
} from './auth.mapper';
import type {
  AuthToken,
  AuthUser,
  EmailConfirmation,
  LoginCredentials,
  PasswordResetRequest,
  RegistrationDetails,
  RegistrationResult,
  SetPasswordRequest,
} from './auth.models';

@Injectable()
export abstract class AuthApi {
  abstract createSession(credentials: LoginCredentials): Observable<AuthToken>;
  abstract destroySession(): Observable<void>;
  abstract renewSession(): Observable<AuthToken>;
  abstract currentUser(): Observable<AuthUser>;
  abstract setWorkspace(companyId: string, branchId: string): Observable<void>;
  abstract requestPasswordReset(request: PasswordResetRequest): Observable<void>;
  abstract setPassword(request: SetPasswordRequest): Observable<void>;
  abstract register(details: RegistrationDetails): Observable<RegistrationResult>;
  abstract confirmEmail(token: string): Observable<EmailConfirmation>;
  abstract resendConfirmation(email: string): Observable<void>;
  abstract setLanguage(language: string): Observable<void>;
}

@Injectable()
export class HttpAuthApi extends AuthApi {
  private readonly http = inject(HttpClient);
  private readonly base = '/auth/session';

  override createSession(credentials: LoginCredentials): Observable<AuthToken> {
    const body: CreateSessionRequest = {
      username: credentials.username.trim(),
      password: credentials.password,
    };

    return this.http
      .post<ApiEnvelope<TokenResponse>>(`${this.base}/create`, body, { context: skipAuth() })
      .pipe(map((response) => toAuthToken(response.data.token)));
  }

  override destroySession(): Observable<void> {
    return this.http
      .get<void>(`${this.base}/destroy`, { context: skipAuth() })
      .pipe(map(() => undefined));
  }

  override renewSession(): Observable<AuthToken> {
    return this.http
      .get<ApiEnvelope<TokenResponse>>(`${this.base}/renew`, { context: skipAuth() })
      .pipe(map((response) => toAuthToken(response.data.token)));
  }

  override currentUser(): Observable<AuthUser> {
    return this.http
      .get<ApiEnvelope<CurrentUserResponse>>(`${this.base}/current`)
      .pipe(map((response) => toAuthUser(response.data, response.meta)));
  }

  override setWorkspace(companyId: string, branchId: string): Observable<void> {
    const body: SetWorkspaceRequest = { merchant_id: companyId, branch_id: branchId };

    return this.http.put<unknown>(`${this.base}/setworkspace`, body).pipe(map(() => undefined));
  }

  override requestPasswordReset(request: PasswordResetRequest): Observable<void> {
    return this.http
      .post<void>(`${this.base}/reset`, request, { context: skipAuth() })
      .pipe(map(() => undefined));
  }

  override setPassword(request: SetPasswordRequest): Observable<void> {
    return this.http
      .post<void>(`${this.base}/init`, request, { context: skipAuth() })
      .pipe(map(() => undefined));
  }

  override register(details: RegistrationDetails): Observable<RegistrationResult> {
    const body: RegisterRequest = toRegisterPayload(details);

    return this.http
      .post<ApiEnvelope<RegisterResponse | null>>(`${this.base}/registerfull`, body, {
        context: skipAuth(),
      })
      .pipe(map((response) => toRegistrationResult(response?.data, body.user.email)));
  }

  override confirmEmail(token: string): Observable<EmailConfirmation> {
    const body: ConfirmEmailRequest = { token };

    return this.http
      .post<ApiEnvelope<ConfirmEmailResponse | null>>(`${this.base}/confirm-email`, body, {
        context: skipAuth(),
      })
      .pipe(map((response) => toEmailConfirmation(response?.data)));
  }

  override resendConfirmation(email: string): Observable<void> {
    const body: ResendConfirmationRequest = { email: email.trim() };

    return this.http
      .post<ApiEnvelope<ResendConfirmationResponse | null>>(
        `${this.base}/confirm-email/resend`,
        body,
        { context: skipAuth() },
      )
      .pipe(map(() => undefined));
  }

  override setLanguage(language: string): Observable<void> {
    const body: SetLanguageRequest = { default_language: language };

    return this.http.put<unknown>(`${this.base}/profile/language`, body).pipe(map(() => undefined));
  }
}

function skipAuth(): HttpContext {
  return new HttpContext().set(SKIP_AUTH, true);
}
